import React, {
	useState, useRef, useEffect, useMemo, useCallback,
} from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useDeepCompareEffect } from 'react-use';
import merge from 'lodash/merge';
import { flatten } from 'flat';
import { convertDataObjectToFields } from '@buddy-technology/ion-helpers';
import {
	Card,
	ProgressBar,
} from '../components/UI';
import {
	Quote,
	Thanks,
	OfferOnly,
	PoweredBy,
} from '../components';
import StripedCheckout from '../components/StripedCheckout';
import { RenderedView, SWITCH, IF } from '../components/helpers';
import { CallProvider } from '../context/CallContext';
import { useDataContext } from '../context/DataContext';
import { useEventContext } from '../context/EventContext';
import {
	filterConditionalFieldsOrViews,
	getMiddleViews,
	getOfferOnlyViews,
	trimValues,
} from '../utils';
import {
	BASE_VIEW_STATUSES,
	INVALID_DISPLAY_TYPES,
	VIEW_TYPES,
} from '../models/dictionary';
import { useNavigate, useSetDynamicValues } from '../hooks';

const InnerForm = ({
	checkoutViewInfo,
	filteredApplication,
	flatApplication,
	includeCheckout,
	initialView,
	ion = {},
	ionValues,
	logoOverride,
	offerOnlyViewData,
	onOptIn,
	onOptOut,
	partnerId,
	quoteFields,
	resolver,
	stage,
	stepThroughViews,
	stripe,
	views,
	viewType,
	placesApiKey,
	theme,
}) => {
	const isFirstLoadRef = useRef(true);
	const { offeringOptions } = useDataContext();
	const { eventCallback } = useEventContext();

	const [isNavShowing, setIsNavShowing] = useState(true);
	const [offerOnlyViews, setOfferOnlyViews] = useState([]);
	const offerOnlyViewIds = offerOnlyViews.map(({ id }) => id);

	const { variables } = ion;
	// we need to keep track of current view in 'single-page' views, but don't want to trigger re-renders as users scroll.
	// so we're using useRef to store/update that value.
	const currentViewRef = useRef(initialView);
	const updateCurrentView = useCallback((id) => { currentViewRef.current = id; }, []);
	const lastNavAction = useRef();
	const updateLastNavAction = useCallback((id) => { lastNavAction.current = id; }, []);

	const defaultValues = { ...ionValues, 'policy::partner': partnerId }; // merge in the partnerId here.

	const {
		register: rhfRegister,
		control,
		handleSubmit,
		watch,
		getValues,
		setValue: rhfSetValue,
		getFieldState,
		trigger,
		reset,
		clearErrors,
		setError,
		formState: {
			errors,
		},
	} = useForm({
		mode: 'onTouched',
		defaultValues,
		resolver: async (values, context, options) => {
			const trimmedValues = trimValues(values);
			// Supply the same Joi context (variables, TODAY) that manual validation uses,
			// otherwise `trigger` evaluates conditions/rules referencing $variables or $TODAY
			// differently than useValidateFields and disagrees on validity.
			const joiContext = { ...context, variables, TODAY: new Date().setHours(-24, 0, 0, 0) };
			return resolver(trimmedValues, joiContext, options);
		},
	});

	const setDynamicValues = useSetDynamicValues({
		getValues,
		getFieldState,
		setValue: rhfSetValue, // useSetDynamicValues needs the raw setValues, otherwise we'll have an infinite loop.
		variables,
		flatApplication,
	});

	const [displayedViews, setDisplayedViews] = useState(
		() => filterConditionalFieldsOrViews(stepThroughViews, getValues(), variables, offeringOptions),
	);
	const [displayedFields, setDisplayedFields] = useState(
		() => filterConditionalFieldsOrViews(flatApplication, getValues(), variables, offeringOptions).map(({ id }) => id),
	);

	// Refs let us expose stable callback identities (updateAppState, setValue, register)
	// while the wrappers always call into the freshest closure. Without this, every
	// blur rebuilt baseViewProps and cascaded a re-render through every child.
	const setDynamicValuesRef = useRef(setDynamicValues);
	setDynamicValuesRef.current = setDynamicValues;

	const updateHiddenElementsRef = useRef();
	updateHiddenElementsRef.current = () => {
		const formValues = getValues();
		const newViews = filterConditionalFieldsOrViews(stepThroughViews, formValues, ion.variables, offeringOptions);
		const newFields = filterConditionalFieldsOrViews(flatApplication, formValues, variables, offeringOptions).map(({ id }) => id);
		const serializeArr = (arr) => arr.map(({ id }) => id).join(',');
		setDisplayedViews((prev) => (serializeArr(prev) !== serializeArr(newViews) ? newViews : prev));
		setDisplayedFields((prev) => (prev.join(',') !== newFields.join(',') ? newFields : prev));
	};

	/**
		@description Updates any dynamic state values (that depend on other values), and dynamically hides/shows fields.
		Only triggers re-render if there's a diff.
	 */
	const updateAppState = useMemo(() => (e) => {
		setDynamicValuesRef.current(e);
		updateHiddenElementsRef.current();
	}, []);

	const setValue = useCallback((key, value) => {
		rhfSetValue(key, value);
		/*
			No need to pass an argument to updateAppState. Arguments are for letting updateAppState to not override an input before its isTouched state has updated. SetValue is used imperatively in this instance, so no need to pass an event object (not that there is one available anyway).
		*/
		updateAppState();
	}, [rhfSetValue, updateAppState]);

	const register = useCallback((name, options = {}) => (
		rhfRegister(name, {
			...options,
			onChange: (e) => {
				if (options.onChange) {
					options.onChange(e);
				}
				const { type } = e?.target || {};
				if (['radio', 'checkbox'].includes(type)) {
					updateAppState(e);
				}
			},
			onBlur: (e) => {
				if (options.onBlur) {
					options.onBlur(e);
				}
				updateAppState(e);
			},
		})
	), [rhfRegister, updateAppState]);

	const quoteViewData = displayedViews.find(({ id, type }) => [id, type?.toLowerCase()].includes('quote'));
	const displayedMiddleViews = getMiddleViews(displayedViews);

	const postCheckoutView = displayedViews.find(({ type }) => type?.toUpperCase() === 'POST_CHECKOUT');

	// we need to update incoming values to the RHF state if parent component updates.
	useDeepCompareEffect(() => {
		if (viewType === VIEW_TYPES.OFFER_ONLY && offerOnlyViewData?.invalidFieldDisplay === INVALID_DISPLAY_TYPES.PAGINATED) {
			const offerViews = getOfferOnlyViews({
				stepThroughViews,
				flatApplication,
				incomingValues: defaultValues,
				offeringOptions,
			});
			setOfferOnlyViews(offerViews);
		}

		const currentValues = getValues();

		const updatedValues = convertDataObjectToFields(offeringOptions.data || {});

		const mergedValues = merge({}, currentValues, updatedValues);
		reset(mergedValues);
		updateAppState();

		// TODO: We should revisit calling RESET before the first load event.
		// if it's the first load, we don't want to reset the form.
		if (isFirstLoadRef.current) {
			isFirstLoadRef.current = false;
			return;
		}

		const updatedDataFieldIds = Object.keys(flatten(offeringOptions.data));

		eventCallback('onOfferElementUpdate', {
			timestamp: Date.now(),
			partnerId,
			updatedDataFieldIds,
		});
	}, [offeringOptions.data]); // offeringOptions.data is our un-mutated incoming data. It updates if the parent updates incoming data.

	// offerOnlyViews only has length if we're in offer only mode and have bad data, otherwise, it's an empty array.
	const startingView = offerOnlyViews.length ? offerOnlyViews[0].id : initialView;

	const { currentView, navigate } = useNavigate({
		getValues,
		variables,
		viewType,
		viewRef: currentViewRef,
		trigger,
		clearErrors,
		// if offer only, start the app in first view returned from getOfferOnlyViews
		initialView: startingView,
		flatApplication,
		stepThroughViews: viewType === VIEW_TYPES.OFFER_ONLY ? offerOnlyViews : stepThroughViews,
		updateLastNavAction,
		displayedFields,
	});

	useEffect(() => {
		navigate(startingView);
		// run through any dynamic field calculations on initialization
		updateAppState();
	}, [ion.id, startingView]);

	const stepOrder = [
		...displayedMiddleViews.map(({ id }) => id),
		BASE_VIEW_STATUSES.CHECKOUT,
		BASE_VIEW_STATUSES.THANKS,
	];

	const handleSuccess = (id) => {
		setValue('orderId', id);
		navigate(postCheckoutView ? postCheckoutView.id : BASE_VIEW_STATUSES.THANKS);
	};

	const showNav = ([VIEW_TYPES.PAGINATED, VIEW_TYPES.OFFER_ONLY].includes(viewType)) && isNavShowing;
	const baseViewProps = useMemo(() => ({
		control,
		clearErrors,
		Controller,
		displayedFields,
		errors,
		flatApplication,
		getFieldState,
		getValues,
		ionId: ion.id,
		initialView: startingView,
		// Pass the ref itself; consumers read .current at call time so ref-only writes
		// (e.g. from useNavigate in single-form) aren't masked by the memoized snapshot.
		lastNavActionRef: lastNavAction,
		navigate,
		placesApiKey,
		register,
		setValue,
		setError,
		showNav,
		stage,
		status: currentView,
		theme,
		toggleNav: setIsNavShowing,
		trigger,
		updateAppState,
		updateCurrentView,
		variables,
		watch,
	}), [
		control, clearErrors, displayedFields, errors, flatApplication,
		getFieldState, getValues, ion.id, startingView, navigate, placesApiKey,
		register, setValue, setError, showNav, stage, currentView, theme,
		trigger, updateAppState, updateCurrentView, variables, watch,
	]);

	const quoteProps = {
		...baseViewProps,
		partnerId,
		filteredApplication,
		quoteRequirements: quoteFields,
		offering: ion.id,
	};

	const steps = displayedMiddleViews.map(({ id, type, ...props }) => (
		(type === 'QUOTE' || id === 'quote') ? (
			<Quote
				{...quoteProps}
				{...props}
				id={id}
				case={id}
				key={id}
				type={type}
				stage={stage}
			/>
		)
			: (
				<RenderedView
					{...baseViewProps}
					{...props}
					id={id}
					case={id}
					key={id}
					type={type}
				/>
			)
	));

	const checkoutStep = (
		<StripedCheckout
			{...baseViewProps}
			id={checkoutViewInfo?.id || 'checkout'}
			key={BASE_VIEW_STATUSES.CHECKOUT}
			case={BASE_VIEW_STATUSES.CHECKOUT}
			stripe={stripe}
			checkoutViewInfo={checkoutViewInfo}
			handleSuccess={handleSuccess}
			includeCheckout={includeCheckout}
			onOptIn={onOptIn}
			handleSubmit={handleSubmit}
			stage={stage}
		/>
	);

	const thanksStep = postCheckoutView ? (
		<RenderedView
			key={BASE_VIEW_STATUSES.THANKS}
			{...postCheckoutView}
			{...baseViewProps}
			case={postCheckoutView.id}
		/>
	)
		: (
			<Thanks
				key={BASE_VIEW_STATUSES.THANKS}
				case={BASE_VIEW_STATUSES.THANKS}
				orderID={getValues('orderId')}
				email={getValues('customer::email')}
			/>
		);

	const allSteps = [
		...steps,
		checkoutStep,
		thanksStep,
	];

	// TODO: Revisit always creating these elements even if used conditionally.
	const offerOnly = (
		<OfferOnly
			{...quoteProps}
			id="offer-only"
			key="offer-only-component"
			onOptIn={onOptIn}
			onOptOut={onOptOut}
			case={BASE_VIEW_STATUSES.OFFER_ONLY_QUOTE}
			viewData={offerOnlyViewData}
			quoteViewData={quoteViewData}
			shouldHideBack={offerOnlyViews.length === 0}
			stage={stage}
			control={control}
		/>
	);

	const offerOnlySteps = [
		offerOnly,
		...allSteps.filter(({ key }) => offerOnlyViewIds.includes(key)),
	];

	const containerId = ion.id.replace(/_/g, '-').toLowerCase();
	// grabbing the whole current view object for conditional rendering below based on view details.
	const currentViewObj = views.find(({ id }) => id === currentViewRef.current);

	const isCheckoutAndHasNoMilestoneProp = currentViewObj?.type === 'CHECKOUT' && !(currentViewObj?.milestone);
	const shouldShowProgressBar = !(currentViewObj?.type === 'OFFER' || isCheckoutAndHasNoMilestoneProp);

	return (
		<div id={containerId}>
			<div id="offer-container">
				<Card
					footer={(
						<div id="powered-by-buddy-container">
							<PoweredBy {...logoOverride} screen={currentView} partner={partnerId} />
						</div>
					)}
				>
					<CallProvider>
						<SWITCH match={viewType}>
							{/* eslint-disable-next-line react/no-unknown-property */}
							<div case={VIEW_TYPES.PAGINATED}>
								<div>
									<IF condition={shouldShowProgressBar}>
										<ProgressBar
											stepOrder={stepOrder}
											currentIndex={stepOrder.indexOf(currentView)}
											views={stepThroughViews}
											currentViewObj={currentViewObj}
											getValues={getValues}
											variables={variables}
										/>
									</IF>
									<SWITCH match={currentView}>
										{allSteps}
									</SWITCH>
								</div>
							</div>

							{/* eslint-disable-next-line react/no-unknown-property */}
							<div case={VIEW_TYPES.SINGLE_FORM}>
								<IF condition={currentView !== BASE_VIEW_STATUSES.THANKS}>
									{allSteps
										.filter(({ key }) => (key !== BASE_VIEW_STATUSES.THANKS))
										.map((step) => (
											<div
												key={`step-${step.key}`}
												className="single-form-view-container"
											>
												{step}
											</div>
										)) }
								</IF>
								<IF condition={currentView === BASE_VIEW_STATUSES.THANKS}>
									{thanksStep}
								</IF>
							</div>

							{/* eslint-disable-next-line react/no-unknown-property */}
							<div case={VIEW_TYPES.OFFER_ONLY}>
								<IF condition={offerOnlyViews.length > 0}>
									<SWITCH match={currentView} test>
										{offerOnlySteps}
									</SWITCH>
								</IF>
								<IF condition={offerOnlyViews.length <= 0}>
									{offerOnly}
								</IF>
							</div>
						</SWITCH>
					</CallProvider>
				</Card>
			</div>
		</div>
	);
};

export default InnerForm;
